import { Component, Inject, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { PermissionsService } from '@shared/services/permissions.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import {
  Permission,
  CreatePermissionPayload,
  UpdatePermissionPayload,
} from '@shared/models/entities/permission.model';

export interface PermissionFormDialogData {
  /** Permiso a editar; ausente = crear. */
  permission?: Permission;
}

/**
 * Diálogo de alta/edición de permiso, sobre el cascarón compartido
 * (`FormDialogShellComponent`): título fijo, acciones fijas, contenido
 * scrolleable. Guarda él mismo y cierra con `true` si hubo cambios.
 */
@Component({
  selector: 'app-permission-form-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    FormDialogShellComponent,
  ],
  templateUrl: './permission-form-dialog.component.html',
  styleUrl: './permission-form-dialog.component.scss',
})
export class PermissionFormDialogComponent {
  private readonly fb                   = inject(FormBuilder);
  private readonly permissionsService   = inject(PermissionsService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly dialogRef            = inject(MatDialogRef<PermissionFormDialogComponent>);

  readonly isEditing: boolean;
  readonly isCreating: boolean;
  saving = signal(false);

  form: FormGroup = this.fb.group({
    name:        ['', [Validators.required, Validators.minLength(2)]],
    description: ['', Validators.required],
    active:      [true],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: PermissionFormDialogData) {
    this.isEditing = !!data?.permission;
    this.isCreating = !this.isEditing;
    if (data?.permission) {
      this.form.patchValue({
        name:        data.permission.name,
        description: data.permission.description,
        active:      data.permission.active,
      });
    }
  }

  fieldError(field: string, error = 'required'): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl?.hasError(error) && ctrl.touched);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, description, active } = this.form.value;

    this.saving.set(true);
    try {
      if (this.isCreating) {
        const payload: CreatePermissionPayload = { name, description, active };
        const created = await this.permissionsService.createPermission(payload);
        await this.customMessageService.open({
          title: '¡Creado!', type: 'success',
          message: `Permiso <b>${created?.name ?? name}</b> creado correctamente.`,
          duration: 2500,
        });
      } else {
        const payload: UpdatePermissionPayload = {
          id: this.data.permission!.id,
          name, description, active,
        };
        const updated = await this.permissionsService.updatePermission(payload);
        await this.customMessageService.open({
          title: '¡Actualizado!', type: 'success',
          message: `Permiso <b>${updated?.name ?? name}</b> actualizado correctamente.`,
          duration: 2500,
        });
      }
      this.dialogRef.close(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ocurrió un error al guardar el permiso.';
      await this.customMessageService.open({
        title: 'Error', type: 'error', message, dismissBtn: true,
      });
    } finally {
      this.saving.set(false);
    }
  }
}
